<template>
  <el-dialog
    title="新增采购记录"
    :visible.sync="dialogVisible"
    width="920px"
    custom-class="manual-purchase-record-dialog"
    :close-on-click-modal="false"
    append-to-body
    @close="handleClose"
  >
    <div class="form-row">
      <span class="form-label required">采购日期</span>
      <el-date-picker
        v-model="form.date"
        type="date"
        size="small"
        value-format="yyyy-MM-dd"
        placeholder="选择日期"
        class="form-date"
      ></el-date-picker>
      <span class="form-label">采购供应商</span>
      <el-select
        v-model="form.supplierId"
        size="small"
        filterable
        clearable
        :placeholder="supplierPlaceholder"
        class="form-supplier"
      >
        <el-option
          v-for="supplier in supplierList"
          :key="supplier.supplierId"
          :label="supplier.supplierShortName || supplier.supplierName"
          :value="String(supplier.supplierId)"
        ></el-option>
      </el-select>
    </div>

    <div class="form-row">
      <span class="form-label required">采购类型</span>
      <el-radio-group v-model="form.purchaseProductType" @change="onPurchaseTypeChange">
        <el-radio :label="1">产品</el-radio>
        <el-radio :label="2">箱子</el-radio>
        <el-radio :label="4">材料</el-radio>
      </el-radio-group>
    </div>

    <div class="add-row" v-if="form.purchaseProductType === 1">
      <el-select
        class="add-product-type"
        size="small"
        filterable
        clearable
        placeholder="产品类型"
        v-model="addForm.productType"
        @change="onProductTypeChange"
      >
        <el-option
          v-for="product in productList"
          :key="product.type || product.productType"
          :label="product.productName"
          :value="product.productType"
        ></el-option>
      </el-select>
      <el-select
        class="add-sku"
        size="small"
        filterable
        clearable
        placeholder="选择 SKU"
        v-model="addForm.sku"
        :disabled="!addForm.productType && addForm.productType !== 0"
        :loading="skuLoading"
        @keyup.enter.native="onAddLine"
      >
        <el-option
          v-for="item in skuOptions"
          :key="item.sku"
          :label="item.label"
          :value="item.sku"
        ></el-option>
      </el-select>
      <el-input
        class="add-qty"
        size="small"
        type="number"
        placeholder="采购数量"
        v-model="addForm.planPurchase"
        @keyup.enter.native="onAddLine"
      ></el-input>
      <el-button type="primary" size="small" :disabled="skuLoading" @click="onAddLine">添加</el-button>
    </div>
    <div class="add-row" v-else-if="form.purchaseProductType === 2">
      <el-select class="add-product-type" size="small" clearable placeholder="选择店铺" v-model="addForm.shopType">
        <el-option v-for="shop in shopOptions" :key="shop.shopType" :label="shop.shopName" :value="shop.shopType"></el-option>
      </el-select>
      <el-select
        class="add-sku"
        size="small"
        filterable
        clearable
        placeholder="选择箱子规格"
        v-model="addForm.boxId"
        :loading="optionLoading"
      >
        <el-option v-for="box in boxOptions" :key="box.boxId" :label="box.label" :value="box.boxId"></el-option>
      </el-select>
      <el-input class="add-qty" size="small" type="number" placeholder="采购数量" v-model="addForm.planPurchase" @keyup.enter.native="onAddLine"></el-input>
      <el-button type="primary" size="small" :disabled="optionLoading" @click="onAddLine">添加</el-button>
    </div>
    <div class="add-row" v-else>
      <el-select class="add-product-type" size="small" clearable placeholder="选择店铺" v-model="addForm.shopType">
        <el-option v-for="shop in shopOptions" :key="shop.shopType" :label="shop.shopName" :value="shop.shopType"></el-option>
      </el-select>
      <el-select
        class="add-sku"
        size="small"
        filterable
        clearable
        placeholder="选择材料"
        v-model="addForm.materialKey"
        :loading="optionLoading"
      >
        <el-option v-for="item in materialOptions" :key="item.key" :label="item.label" :value="item.key"></el-option>
      </el-select>
      <el-input class="add-qty" size="small" type="number" placeholder="采购数量" v-model="addForm.planPurchase" @keyup.enter.native="onAddLine"></el-input>
      <el-button type="primary" size="small" :disabled="optionLoading" @click="onAddLine">添加</el-button>
    </div>
    <div class="add-tip">{{ addTip }}</div>

    <el-table :data="lines" border stripe size="mini" max-height="360" show-summary :summary-method="getSummaries">
      <el-table-column label="序号" width="50" align="center">
        <template slot-scope="scope">{{ scope.$index + 1 }}</template>
      </el-table-column>
      <el-table-column prop="shopName" label="店铺" width="90" align="center" show-overflow-tooltip></el-table-column>
      <el-table-column v-if="form.purchaseProductType === 1" prop="productName" label="产品名称" min-width="120" show-overflow-tooltip></el-table-column>
      <el-table-column v-if="form.purchaseProductType === 1" prop="sku" label="SKU" min-width="160" show-overflow-tooltip></el-table-column>
      <el-table-column v-if="form.purchaseProductType === 1" prop="purchaseColorName" label="颜色" width="80" align="center" show-overflow-tooltip></el-table-column>
      <el-table-column v-if="form.purchaseProductType === 1" prop="model" label="机型" width="90" align="center" show-overflow-tooltip></el-table-column>
      <el-table-column v-if="form.purchaseProductType === 2" prop="boxSpec" label="箱子规格" min-width="220" show-overflow-tooltip></el-table-column>
      <el-table-column v-if="form.purchaseProductType === 2" prop="boxId" label="箱子编号" min-width="150" show-overflow-tooltip></el-table-column>
      <el-table-column v-if="form.purchaseProductType === 4" prop="materialName" label="材料名称" min-width="220" show-overflow-tooltip></el-table-column>
      <el-table-column prop="planPurchase" label="采购数量" width="120" align="center">
        <template slot-scope="scope">
          <el-input
            type="number"
            size="mini"
            v-model="scope.row.planPurchase"
            @blur="onLineQtyBlur(scope.row)"
          ></el-input>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="70" align="center">
        <template slot-scope="scope">
          <el-button type="text" size="mini" @click="removeLine(scope.$index)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="remark-row">
      <span class="remark-label">备注</span>
      <el-input
        type="textarea"
        :rows="2"
        size="small"
        maxlength="500"
        show-word-limit
        v-model="form.remark"
        placeholder="选填"
      ></el-input>
    </div>

    <div slot="footer" class="dialog-footer">
      <el-button size="small" @click="dialogVisible = false">取 消</el-button>
      <el-button type="primary" size="small" :loading="submitting" @click="onSubmit">确 定</el-button>
    </div>
  </el-dialog>
</template>

<script>
import { requestModules } from "@r";
import { activeProducts, allActiveProducts, colors } from "../../utils/constant.js";

function parsePlanPurchase(raw) {
  if (raw === "" || raw == null) return { error: "请填写采购数量" };
  const text = String(raw).trim();
  if (!text) return { error: "请填写采购数量" };
  if (text.includes(".")) return { error: "采购数量必须为正整数" };
  const qtyNum = Number(text);
  if (!Number.isFinite(qtyNum) || !Number.isInteger(qtyNum) || qtyNum <= 0) {
    return { error: "采购数量必须为正整数" };
  }
  return { qty: qtyNum };
}

function todayYmd() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default {
  name: "CreateManualPurchaseRecordDialog",
  props: {
    supplierList: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  data() {
    return {
      dialogVisible: false,
      productList: localStorage.getItem("shopType") == 6 ? allActiveProducts : activeProducts,
      colors: colors,
      shopOptions: [
        { shopType: 1, shopName: "灵柚" },
        { shopType: 2, shopName: "萧发" },
        { shopType: 3, shopName: "誉腾" },
        { shopType: 4, shopName: "甄昂" },
        { shopType: 8, shopName: "众联" },
      ],
      form: {
        date: todayYmd(),
        supplierId: "",
        remark: "",
        purchaseProductType: 1,
      },
      addForm: {
        productType: "",
        sku: "",
        planPurchase: "",
        boxId: "",
        shopType: "",
        materialKey: "",
      },
      skuOptions: [],
      skuLoading: false,
      boxOptions: [],
      materialOptions: [],
      optionLoading: false,
      lines: [],
      submitting: false,
    };
  },
  computed: {
    supplierPlaceholder() {
      if (this.form.purchaseProductType === 4) return "可选，不选则按材料默认供应商";
      if (this.form.purchaseProductType === 1) return "可选，不选则按材料默认供应商";
      return "可选";
    },
    addTip() {
      if (this.form.purchaseProductType === 2) {
        return "选择店铺和启用中的箱子规格。产品名称记为纸箱，SKU 为规格；同日同店铺同一规格已存在将拦截。";
      }
      if (this.form.purchaseProductType === 4) {
        return "选择店铺和材料；有颜色的材料按颜色分行。不选供应商时使用材料默认供应商。同日同店铺同一材料已存在将拦截。";
      }
      return "店铺/颜色/机型由 SKU 主数据带出；同日同 SKU 同店铺已存在将拦截。";
    },
  },
  methods: {
    open() {
      this.dialogVisible = true;
      this.resetForm();
    },
    resetForm() {
      this.form = {
        date: todayYmd(),
        supplierId: "",
        remark: "",
        purchaseProductType: 1,
      };
      this.addForm = { productType: "", sku: "", planPurchase: "", boxId: "", shopType: "", materialKey: "" };
      this.skuOptions = [];
      this.lines = [];
      this.submitting = false;
    },
    handleClose() {
      this.resetForm();
    },
    onProductTypeChange() {
      this.addForm.sku = "";
      this.skuOptions = [];
      if (this.addForm.productType === "" || this.addForm.productType == null) return;
      this.skuLoading = true;
      requestModules
        .fetchProducts({
          productType: this.addForm.productType,
          beginIndex: 0,
          pageSize: 10000,
        })
        .then(res => {
          const list = res.ldata || [];
          this.skuOptions = list
            .filter(item => item && String(item.status || "") === "Active")
            .map(item => {
              const sku = String(item["seller-sku"] || item.sku || "").trim();
              const color = item.purchaseColorName || item.productColorName || "";
              const model = item.model || "";
              return {
                sku,
                label: [sku, color, model].filter(Boolean).join(" / "),
                product: item,
              };
            })
            .filter(item => item.sku)
            .sort((a, b) => (a.sku > b.sku ? 1 : -1));
        })
        .catch(err => {
          this.$message.error((err && err.message) || "加载 SKU 失败");
        })
        .finally(() => {
          this.skuLoading = false;
        });
    },
    onPurchaseTypeChange() {
      this.lines = [];
      this.addForm = { productType: "", sku: "", planPurchase: "", boxId: "", shopType: "", materialKey: "" };
      this.skuOptions = [];
      if (this.form.purchaseProductType === 2) this.loadBoxes();
      if (this.form.purchaseProductType === 4) this.loadMaterials();
    },
    loadBoxes() {
      if (this.boxOptions.length) return;
      this.optionLoading = true;
      requestModules
        .fetchOutStockBoxs({ status: 1 })
        .then(res => {
          this.boxOptions = (res.ldata || [])
            .filter(box => box && ~~box.status === 1 && box.boxId)
            .map(box => ({
              boxId: String(box.boxId),
              label: `${box.length}*${box.width}*${box.height} _ (${box.bubbleWeight}KG)`,
            }));
        })
        .catch(err => {
          this.$message.error((err && err.message) || "加载箱子规格失败");
        })
        .finally(() => {
          this.optionLoading = false;
        });
    },
    loadMaterials() {
      if (this.materialOptions.length) return;
      this.optionLoading = true;
      requestModules
        .fetchMaterials({ pageSize: 10000 })
        .then(res => {
          this.materialOptions = this.buildMaterialOptions(res.ldata || []);
        })
        .catch(err => {
          this.$message.error((err && err.message) || "加载材料失败");
        })
        .finally(() => {
          this.optionLoading = false;
        });
    },
    getColorLabel(colorType) {
      const color = this.colors.find(item => item.key == colorType);
      return color ? color.name : "";
    },
    buildMaterialOptions(materials) {
      const list = [];
      (materials || []).forEach(material => {
        if (!material || material.materialId == null || material.materialId === "") return;
        const name = material.materialName || String(material.materialId);
        const colorList = Array.isArray(material.colorList) ? material.colorList : [];
        if (colorList.length) {
          colorList.forEach(color => {
            const colorName = this.getColorLabel(color);
            list.push({
              key: material.materialId + "-" + color,
              label: colorName ? name + "-" + colorName : name,
              materialId: String(material.materialId),
              color: String(color),
              materialName: colorName ? name + "-" + colorName : name,
            });
          });
          return;
        }
        if (material.colorType) {
          const colorName = this.getColorLabel(material.colorType);
          list.push({
            key: material.materialId + "-" + material.colorType,
            label: colorName ? name + "-" + colorName : name,
            materialId: String(material.materialId),
            color: String(material.colorType),
            materialName: colorName ? name + "-" + colorName : name,
          });
          return;
        }
        list.push({
          key: String(material.materialId),
          label: name,
          materialId: String(material.materialId),
          color: "",
          materialName: name,
        });
      });
      return list.sort((a, b) => (a.label > b.label ? 1 : -1));
    },
    onAddLine() {
      if (!this.form.date) {
        this.$message.warning("请先选择采购日期");
        return;
      }
      const parsed = parsePlanPurchase(this.addForm.planPurchase);
      if (parsed.error) {
        this.$message.warning(parsed.error);
        return;
      }
      if (this.form.purchaseProductType === 2) {
        this.addBoxLine(parsed.qty);
        return;
      }
      if (this.form.purchaseProductType === 4) {
        this.addMaterialLine(parsed.qty);
        return;
      }
      this.addProductLine(parsed.qty);
    },
    addProductLine(qty) {
      const sku = String(this.addForm.sku || "").trim();
      if (!sku) {
        this.$message.warning("请选择 SKU");
        return;
      }
      if (this.lines.some(line => line.sku === sku)) {
        this.$message.warning(`SKU「${sku}」已添加`);
        return;
      }
      const option = this.skuOptions.find(item => item.sku === sku);
      const product = (option && option.product) || {};
      this.lines.push({
        sku,
        shopName: product.shopName || "",
        productName: product.productName || "",
        purchaseColorName: product.purchaseColorName || product.productColorName || "",
        model: product.model || "",
        planPurchase: qty,
      });
      this.addForm.sku = "";
      this.addForm.planPurchase = "";
    },
    selectedShop() {
      const shopType = ~~this.addForm.shopType;
      return this.shopOptions.find(item => item.shopType === shopType) || null;
    },
    addBoxLine(qty) {
      const boxId = String(this.addForm.boxId || "").trim();
      const shop = this.selectedShop();
      if (!shop) {
        this.$message.warning("请选择店铺");
        return;
      }
      if (!boxId) {
        this.$message.warning("请选择箱子规格");
        return;
      }
      const box = this.boxOptions.find(item => item.boxId === boxId);
      const boxSpec = box ? box.label : boxId;
      if (this.lines.some(line => line.shopType === shop.shopType && line.boxSpec === boxSpec)) {
        this.$message.warning("该店铺的箱子规格已添加");
        return;
      }
      this.lines.push({
        boxId,
        boxSpec,
        shopType: shop.shopType,
        shopName: shop.shopName,
        planPurchase: qty,
      });
      this.addForm.boxId = "";
      this.addForm.planPurchase = "";
    },
    addMaterialLine(qty) {
      const key = String(this.addForm.materialKey || "").trim();
      const shop = this.selectedShop();
      if (!shop) {
        this.$message.warning("请选择店铺");
        return;
      }
      if (!key) {
        this.$message.warning("请选择材料");
        return;
      }
      if (this.lines.some(line => line.materialKey === key && line.shopType === shop.shopType)) {
        this.$message.warning("该店铺的材料已添加");
        return;
      }
      const material = this.materialOptions.find(item => item.key === key);
      if (!material) {
        this.$message.warning("请选择材料");
        return;
      }
      this.lines.push({
        materialKey: material.key,
        materialId: material.materialId,
        color: material.color,
        materialName: material.materialName,
        shopType: shop.shopType,
        shopName: shop.shopName,
        planPurchase: qty,
      });
      this.addForm.materialKey = "";
      this.addForm.planPurchase = "";
    },
    onLineQtyBlur(row) {
      const parsed = parsePlanPurchase(row.planPurchase);
      if (parsed.error) {
        this.$message.warning(parsed.error);
        return;
      }
      row.planPurchase = parsed.qty;
    },
    removeLine(index) {
      this.lines.splice(index, 1);
    },
    validateForm() {
      if (!this.form.date) {
        this.$message.warning("请选择采购日期");
        return false;
      }
      if (!this.lines.length) {
        this.$message.warning("请至少添加一条明细");
        return false;
      }
      const type = this.form.purchaseProductType;
      const seen = new Set();
      for (let i = 0; i < this.lines.length; i++) {
        const row = this.lines[i];
        const parsed = parsePlanPurchase(row.planPurchase);
        const label = type === 2 ? row.boxSpec || row.boxId : type === 4 ? row.materialName : row.sku;
        if (parsed.error) {
          this.$message.warning(`${label || "第 " + (i + 1) + " 行"}${parsed.error}`);
          return false;
        }
        row.planPurchase = parsed.qty;
        if ((type === 2 || type === 4) && !row.shopType) {
          this.$message.warning(`第 ${i + 1} 行请选择店铺`);
          return false;
        }
        let key = "";
        if (type === 2) key = String(row.boxId || "") + "|" + String(row.shopType || "");
        else if (type === 4) key = String(row.materialKey || "") + "|" + String(row.shopType || "");
        else key = String(row.sku || "").trim();
        if (!key) {
          this.$message.warning(`第 ${i + 1} 行明细不完整`);
          return false;
        }
        if (seen.has(key)) {
          this.$message.warning(`「${label}」重复，请合并后提交`);
          return false;
        }
        seen.add(key);
      }
      return true;
    },
    getSummaries({ columns, data }) {
      const sums = [];
      columns.forEach((column, index) => {
        if (index === 0) {
          sums[index] = "合计";
          return;
        }
        if (column.property === "planPurchase") {
          sums[index] = (data || []).reduce((s, row) => s + (Number(row.planPurchase) || 0), 0);
          return;
        }
        sums[index] = "";
      });
      return sums;
    },
    buildSubmitLines() {
      const type = this.form.purchaseProductType;
      if (type === 2) {
        return this.lines.map(line => ({
          boxId: line.boxId,
          shopType: line.shopType,
          planPurchase: Number(line.planPurchase),
        }));
      }
      if (type === 4) {
        return this.lines.map(line => ({
          materialId: line.materialId,
          color: line.color || "",
          shopType: line.shopType,
          planPurchase: Number(line.planPurchase),
        }));
      }
      return this.lines.map(line => ({ sku: line.sku, planPurchase: Number(line.planPurchase) }));
    },
    onSubmit() {
      if (this.submitting) return;
      if (!this.validateForm()) return;
      this.submitting = true;
      requestModules
        .addManualPurchaseRecord({
          date: this.form.date,
          supplierId: this.form.supplierId || "",
          remark: this.form.remark || "",
          purchaseProductType: this.form.purchaseProductType,
          lines: this.buildSubmitLines(),
        })
        .then(res => {
          if (res.result != 1 && res.code != 0) {
            this.$message.error(res.message || "新增失败");
            return;
          }
          this.$message.success(res.message || "新增成功");
          this.dialogVisible = false;
          this.$emit("success", res.mdata || {});
        })
        .catch(err => {
          this.$message.error((err && err.message) || "新增失败");
        })
        .finally(() => {
          this.submitting = false;
        });
    },
  },
};
</script>

<style lang="scss">
.manual-purchase-record-dialog {
  .form-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
  }
  .form-label {
    color: #606266;
    white-space: nowrap;
    &.required::before {
      content: "*";
      color: #f56c6c;
      margin-right: 2px;
    }
  }
  .form-date {
    width: 160px;
  }
  .form-supplier {
    width: 260px;
  }
  .el-radio-group {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 16px;
  }
  .add-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
  }
  .add-product-type {
    width: 180px;
  }
  .add-sku {
    flex: 1;
    min-width: 240px;
  }
  .add-qty {
    width: 120px;
  }
  .add-tip {
    color: #909399;
    font-size: 12px;
    margin-bottom: 12px;
  }
  .remark-row {
    display: flex;
    align-items: flex-start;
    margin-top: 12px;
  }
  .remark-label {
    width: 48px;
    line-height: 32px;
    color: #606266;
    flex-shrink: 0;
  }
}
</style>
